import { Controller } from '@hotwired/stimulus'

// Connects to data-controller="print-selector"
export default class extends Controller {
  static targets = ['gridContainer', 'printOption', 'expandedContent', 'closeBtn', 'currentPrice']
  static values = {
    options: Object,
    imageUrl: String,
  }

  connect() {
    this.currentExpanded = null
    this.selectedOptions = {}
    this.basePrices = {
      framed_prints: 65,
      photo_prints: 25,
      fine_art_prints: 45,
      standard_canvas: 55,
      metal_prints: 85,
    }
  }

  expandOption(event) {
    event.preventDefault()
    event.stopPropagation()

    // Prevent expansion if clicking on interactive elements
    if (event.target.closest('.size-option')
      || event.target.closest('.style-option')
      || event.target.closest('.close-btn')) {
      return
    }

    const clickedOption = event.currentTarget
    const printType = clickedOption.dataset.printSelectorType

    // Close currently expanded option if different
    if (this.currentExpanded && this.currentExpanded !== clickedOption) {
      this.closeExpanded()
    }

    // Toggle expansion
    if (clickedOption.classList.contains('expanded')) {
      this.closeExpanded()
    }
    else {
      this.expandCurrentOption(clickedOption, printType)
    }
  }

  expandCurrentOption(option, printType) {
    // Add expanded class and styling
    option.classList.add('expanded', 'col-span-full', 'row-span-1')

    // Show expanded content and close button
    const expandedContent = option.querySelector('[data-print-selector-target="expandedContent"]')
    const closeBtn = option.querySelector('[data-print-selector-target="closeBtn"]')

    if (expandedContent) expandedContent.classList.remove('hidden')
    if (closeBtn) closeBtn.classList.remove('hidden')

    // Update current expanded reference
    this.currentExpanded = option

    // Initialize selected options for this print type
    if (!this.selectedOptions[printType]) {
      this.selectedOptions[printType] = {
        size: null,
        style: null,
        price: this.basePrices[printType],
      }
    }

    // Update price display
    this.updatePriceDisplay(option, this.selectedOptions[printType].price)

    // Smooth scroll to expanded option
    setTimeout(() => {
      const menuHeight = 80 // Adjust to your menu bar height in pixels
      const elementTop = option.getBoundingClientRect().top + window.pageYOffset
      const offsetPosition = elementTop - menuHeight

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      })
    }, 300)

    // Dispatch custom event for external listeners
    this.dispatch('optionExpanded', {
      detail: { printType, element: option },
    })
  }

  closeExpanded(event) {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }

    if (!this.currentExpanded) return

    // Remove expanded styling
    this.currentExpanded.classList.remove('expanded', 'col-span-full', 'row-span-1')

    // Hide expanded content and close button
    const expandedContent = this.currentExpanded.querySelector('[data-print-selector-target="expandedContent"]')
    const closeBtn = this.currentExpanded.querySelector('[data-print-selector-target="closeBtn"]')

    if (expandedContent) expandedContent.classList.add('hidden')
    if (closeBtn) closeBtn.classList.add('hidden')

    // Clear current expanded reference
    const previousExpanded = this.currentExpanded
    this.currentExpanded = null

    // Dispatch custom event
    this.dispatch('optionClosed', {
      detail: { element: previousExpanded },
    })
  }

  selectSize(event) {
    event.preventDefault()
    event.stopPropagation()

    const sizeElement = event.currentTarget
    const size = sizeElement.dataset.size
    const price = parseInt(sizeElement.dataset.price)
    const printOption = sizeElement.closest('[data-print-selector-target="printOption"]')
    const printType = printOption.dataset.printSelectorType

    // Remove selected class from siblings
    const siblingOptions = sizeElement.parentElement.querySelectorAll('.size-option')
    siblingOptions.forEach(el => el.classList.remove('selected', 'border-blue-500', 'bg-blue-50', 'border-green-500', 'bg-green-50', 'border-purple-500', 'bg-purple-50', 'border-orange-500', 'bg-orange-50', 'border-gray-600', 'bg-gray-50'))

    // Add selected styling based on print type
    const colorClasses = this.getColorClasses(printType)
    sizeElement.classList.add('selected', ...colorClasses)

    // Update selected options
    if (!this.selectedOptions[printType]) {
      this.selectedOptions[printType] = {}
    }
    this.selectedOptions[printType].size = size
    this.selectedOptions[printType].price = price

    // Update price display
    this.updatePriceDisplay(printOption, price)

    // Dispatch custom event
    this.dispatch('sizeSelected', {
      detail: { printType, size, price },
    })
  }

  selectStyle(event) {
    event.preventDefault()
    event.stopPropagation()

    const styleElement = event.currentTarget
    const style = styleElement.dataset.style
    const printOption = styleElement.closest('[data-print-selector-target="printOption"]')
    const printType = printOption.dataset.printSelectorType

    // Remove selected class from siblings
    const siblingOptions = styleElement.parentElement.querySelectorAll('.style-option')
    siblingOptions.forEach(el => el.classList.remove('selected', 'border-blue-500', 'border-green-500', 'border-purple-500', 'border-orange-500', 'border-gray-600', 'scale-110'))

    // Add selected styling
    const borderColor = this.getBorderColor(printType)
    styleElement.classList.add('selected', borderColor, 'scale-110')

    // Update selected options
    if (!this.selectedOptions[printType]) {
      this.selectedOptions[printType] = {}
    }
    this.selectedOptions[printType].style = style

    // Dispatch custom event
    this.dispatch('styleSelected', {
      detail: { printType, style },
    })
  }

  updatePriceDisplay(printOption, price) {
    const priceDisplay = printOption.querySelector('[data-print-selector-target="currentPrice"]')
    if (priceDisplay) {
      priceDisplay.textContent = `$${price}.00`
    }
  }

  getColorClasses(printType) {
    const colorMap = {
      framed_prints: ['border-blue-500', 'bg-blue-50'],
      photo_prints: ['border-green-500', 'bg-green-50'],
      fine_art_prints: ['border-purple-500', 'bg-purple-50'],
      standard_canvas: ['border-orange-500', 'bg-orange-50'],
      metal_prints: ['border-gray-600', 'bg-gray-50'],
    }
    return colorMap[printType] || ['border-blue-500', 'bg-blue-50']
  }

  getBorderColor(printType) {
    const colorMap = {
      framed_prints: 'border-blue-500',
      photo_prints: 'border-green-500',
      fine_art_prints: 'border-purple-500',
      standard_canvas: 'border-orange-500',
      metal_prints: 'border-gray-600',
    }
    return colorMap[printType] || 'border-blue-500'
  }

  // Public method to get current selections
  getSelectedOptions() {
    return this.selectedOptions
  }

  // Public method to get current expanded option
  getCurrentExpandedType() {
    return this.currentExpanded ? this.currentExpanded.dataset.printSelectorType : null
  }

  // Handle clicks outside expanded options
  handleOutsideClick = (event) => {
    if (this.currentExpanded && !this.element.contains(event.target)) {
      this.closeExpanded()
    }
  }

  // Add outside click listener when an option is expanded
  optionExpandedValueChanged() {
    if (this.currentExpanded) {
      document.addEventListener('click', this.handleOutsideClick)
    }
    else {
      document.removeEventListener('click', this.handleOutsideClick)
    }
  }

  // Method to programmatically set selections (useful for form integration)
  setSelection(printType, options = {}) {
    if (!this.selectedOptions[printType]) {
      this.selectedOptions[printType] = {}
    }

    Object.assign(this.selectedOptions[printType], options)

    // Update UI if the option is currently expanded
    const printOption = this.element.querySelector(`[data-print-selector-type="${printType}"]`)
    if (printOption && printOption.classList.contains('expanded')) {
      if (options.price) {
        this.updatePriceDisplay(printOption, options.price)
      }
    }
  }

  // Method to reset all selections
  resetSelections() {
    this.selectedOptions = {}

    // Remove all selected classes
    this.element.querySelectorAll('.selected').forEach((el) => {
      el.classList.remove('selected', 'border-blue-500', 'bg-blue-50', 'border-green-500', 'bg-green-50', 'border-purple-500', 'bg-purple-50', 'border-orange-500', 'bg-orange-50', 'border-gray-600', 'bg-gray-50', 'scale-110')
    })

    // Reset all prices to base prices
    Object.keys(this.basePrices).forEach((printType) => {
      const printOption = this.element.querySelector(`[data-print-selector-type="${printType}"]`)
      if (printOption) {
        this.updatePriceDisplay(printOption, this.basePrices[printType])
      }
    })

    this.dispatch('selectionsReset')
  }

  // Method to validate current selections
  validateSelections() {
    const errors = []

    Object.keys(this.selectedOptions).forEach((printType) => {
      const selection = this.selectedOptions[printType]
      if (!selection.size) {
        errors.push(`Please select a size for ${printType.replace('_', ' ')}`)
      }
    })

    return {
      isValid: errors.length === 0,
      errors: errors,
    }
  }

  // Method to get formatted selections for form submission
  getFormattedSelections() {
    const formatted = {}

    Object.keys(this.selectedOptions).forEach((printType) => {
      const selection = this.selectedOptions[printType]
      formatted[printType] = {
        size: selection.size,
        style: selection.style,
        price: selection.price,
        image_url: this.imageUrlValue,
      }
    })

    return formatted
  }

  // Cleanup method
  disconnect() {
    document.removeEventListener('click', this.handleOutsideClick)
  }
}
